import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { OrgsService } from "@app2/account/orgs.service";
import { AssetClientService } from "@app2/clients/asset-client.service";
import { RestClient, unwrapIfAppropriate } from "@app2/clients/rest-client";
import { Aggregation2dResult, AggregationResult } from "@app2/search/events-page/aggregation/type-defs";
import { ASSET_TAG_QUERY_REGEX } from "@app2/search/query";
import { EnvironmentService } from "@app2/shared/services/environment.service";
import {
    Aggregation2dRow,
    CrossOrgSearch,
    CrossOrgSearchRequest,
    CrossOrgSearchResults,
    EditWatchlistItemsResponse,
    GeoResponse,
    SavedQuery,
    SearchParams,
    SearchRangeRequest,
    SearchRangeResponse,
    SearchResponse,
    SearchTermWithCount,
    Watchlist,
    WatchlistConfig,
    WatchlistItem,
    WatchlistItems,
    WatchlistType,
} from "@app2/type-defs/search/search-types";
import { QueryObject, toSearchParams, toSearchQuery } from "@app2/util/search-utils";
import { from, Observable } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { ThreatSource } from "@app2/clients/threat-client.service";
import { FeaturesService } from "@app2/shared/services/features.service";

@Injectable({
    providedIn: "root",
})
export class SearchClientService {
    private searchClient: RestClient;
    private searchClientNoOrg: RestClient;

    constructor(httpClient: HttpClient,
                envService: EnvironmentService,
                orgsService: OrgsService,
                private assetClient: AssetClientService,
                private featuresService: FeaturesService) {
        const baseUrl = envService.getConfig().searchService;

        this.searchClient = new RestClient(baseUrl, httpClient, orgsService);
        this.searchClientNoOrg = new RestClient(baseUrl, httpClient);
    }

    public searchRange(request: SearchRangeRequest): Promise<SearchRangeResponse> {
        const body = SearchClientService.toSearchParams(request);
        return this.searchClient.doPost("/search/range", body, null, ["hits", "totalHits"])
            .then(({ hits, totalHits }) => ({
                hits: hits.map(hit => ({...hit.source, _id: hit.id })),
                totalHits: totalHits,
            }));
    }

    public searchRangeWithFinalQuery(searchParams: SearchParams): Promise<SearchResponse> {
        return this.determineFinalQuery(searchParams.query.query_string)
            .then(finalQuery => {
                let body = { ...searchParams, query: { ...searchParams.query, query_string: finalQuery } };
                return this.searchClient.doPost("/search/range", body, undefined, ["hits"])
                    .then(({ hits, totalHits }) => ({
                        entries: hits.map(hit => ({ ...hit.source, _id: hit.id })),
                        total: totalHits,
                    }));
            });
    }

    public searchByRange$(request: SearchRangeRequest): Observable<SearchRangeResponse> {
        return from(this.determineFinalQuery(request.queryString))
            .pipe(
                switchMap(finalQuery => {
                    const baseParams = SearchClientService.toSearchParams(request);
                    const params = { ...baseParams, query: { ...baseParams.query, query_string: finalQuery } };
                    return this.searchClient.doPost$("/search/range", params, undefined, ["hits"]);
                }),
                map(({ hits, totalHits }) => ({
                    hits: hits.map(hit => ({ ...hit.source, _id: hit.id })),
                    totalHits,
                })),
            );
    }

    public downloadCsv(request: Partial<SearchRangeRequest>): Promise<Blob> {
        return this.determineFinalQuery(request.queryString)
            .then(finalQuery => {
                const finalRequest: Partial<SearchRangeRequest> = {
                    ...request,
                    queryString: finalQuery,
                };
                const body = SearchClientService.toSearchParams(finalRequest);
                return this.searchClient.downloadCsvPost("/search/range", body);
            });
    }

    public searchAggregation1dCsv(query: QueryObject, primaryField: string, size?: number, statistical?: boolean): Promise<Blob> {
        return this.determineFinalQuery(query.query_string)
            .then(finalQuery => {
                const body = { ...query, query_string: finalQuery };
                return this.searchClient.downloadCsvPost("/search/aggregation/terms", body,
                    { primaryField, size, statistical });
            });
    }

    public searchAggregation2dCsv(query: QueryObject, primaryField: string,
                                  secondaryField: string, view: string, statistical?: boolean): Promise<Blob> {
        return this.determineFinalQuery(query.query_string)
            .then(finalQuery => {
                const body = { ...query, query_string: finalQuery };
                return this.searchClient.downloadCsvPost("/search/aggregation/2d/terms", body,
                    { primaryField, secondaryField, statistical, view });
            });
    }

    public getAllSavedQueries(includeDeleted: boolean = false, includeLibrary: boolean = false): Promise<SavedQuery[]> {
        return this.searchClient.doPost("/saved-query/list", null, { includeDeleted, includeLibrary })
            .then(unwrapIfAppropriate);
    }

    public getLibrarySavedQueries(includeDeleted: boolean = false): Promise<SavedQuery[]> {
        return this.searchClientNoOrg.doPost("/org/all/saved-query/list", null, { includeDeleted })
            .then(unwrapIfAppropriate);
    }

    public createSavedQuery(savedQuery: SavedQuery): Promise<SavedQuery> {
        return this.searchClient.doPost("/saved-query", savedQuery).then(unwrapIfAppropriate);
    }

    public updateSavedQuery(savedQuery: SavedQuery): Promise<SavedQuery> {
        return this.searchClient.doPut(`/saved-query/${savedQuery.id}`, savedQuery);
    }

    public promoteSavedQuery(savedQuery: SavedQuery): Promise<SavedQuery> {
        return this.searchClient.doPut("/promote", savedQuery);
    }

    public deleteSavedQueries(savedQueryIds: uuid[]): Promise<uuid[]> {
        return this.searchClient.doPost(`/delete-saved-queries`, savedQueryIds);
    }

    public restoreSavedQuery(savedQueryId: uuid): Promise<uuid[]> {
        return this.searchClient.doPost(`/saved-query/${savedQueryId}/restore`);
    }

    public createLibrarySavedQuery(savedQuery: SavedQuery): Promise<SavedQuery> {
        return this.searchClientNoOrg.doPost("/org/all/saved-query", savedQuery).then(unwrapIfAppropriate);
    }

    public updateLibrarySavedQuery(savedQuery: SavedQuery): Promise<SavedQuery> {
        return this.searchClientNoOrg.doPut(`/org/all/saved-query/${savedQuery.id}`, savedQuery);
    }

    public deleteLibrarySavedQuery(savedQueryIds: uuid[]): Promise<uuid[]> {
        return this.searchClientNoOrg.doDelete(`/org/all/saved-query/`, {"savedQueryId": savedQueryIds});
    }

    public restoreLibrarySavedQuery(savedQueryId: uuid): Promise<SavedQuery> {
        return this.searchClientNoOrg.doPost(`/org/all/saved-query/${savedQueryId}/restore`);
    }

    public getAllFieldsForAllOrgs(includeNested = false): Promise<string[]> {
        return this.searchClientNoOrg.doGet("/fields", { includeNested });
    }

    public getAllFields(includeNested = false): Promise<string[]> {
        return this.searchClient.doGet("/fields", { includeNested }).then(unwrapIfAppropriate);
    }

    public getSortableFields(): Promise<string[]> {
        return this.searchClient.doGet("/sortableFields").then(unwrapIfAppropriate);
    }

    public searchAggregation1d$(query: QueryObject, primaryField: string, size?: number, statistical?: boolean): Observable<AggregationResult> {
        return from(this.determineFinalQuery(query.query_string))
            .pipe(switchMap(finalQuery => this.searchClient.doPost$<AggregationResult>(`/search/aggregation/terms`,
                { ...query, query_string: finalQuery },
                { primaryField, size, statistical },
                ["results"])));
    }

    public searchAggregation2d$(query: QueryObject, primaryField: string, secondaryField: string,
                                statistical?: boolean): Observable<Aggregation2dResult> {
        return from(this.determineFinalQuery(query.query_string))
            .pipe(switchMap(finalQuery => this.searchClient.doPost<Aggregation2dResult>(`/search/aggregation/2d/terms`,
                { ...query, query_string: finalQuery },
                { primaryField, secondaryField, statistical },
                ["results"])));
    }

    public searchAggregationDateHistogram$(query: QueryObject, interval: string): Observable<SearchTermWithCount[]> {
        return from(this.determineFinalQuery(query.query_string))
            .pipe(switchMap(finalQuery => this.searchClient.doPost<SearchTermWithCount[]>("/search/aggregation/date-histogram",
                { ...query, query_string: finalQuery },
                { interval })));
    }

    public searchDateHistogramThenTerms(query: QueryObject, primaryField: string, interval: string): Promise<Array<Aggregation2dRow<date>>> {
        return this.determineFinalQuery(query.query_string)
            .then(finalQuery => {
                const body = { ...query, query_string: finalQuery };
                return this.searchClient.doPost(`/search/aggregation/2d/date-histogram`,
                    body,
                    { primaryField, interval },
                    ["sub_terms"]);
            });
    }

    public searchAggregationDateHistogram(query: QueryObject, interval: string): Promise<SearchTermWithCount[]> {
        return this.searchAggregationDateHistogram$(query, interval).toPromise();
    }

    public geoSearch(query: QueryObject): Promise<GeoResponse> {
        return this.determineFinalQuery(query.query_string)
            .then(finalQuery => {
                const body = { ...query, query_string: finalQuery };
                return this.searchClient.doPost("/search/aggregation/geo", body);
            });
    }

    public crossOrgSearch(request: CrossOrgSearchRequest): Promise<CrossOrgSearch> {
        return this.searchClientNoOrg.doPost("/cross-org-search", request);
    }

    public getCrossOrgSearchResults(id: uuid): Promise<CrossOrgSearchResults> {
        return this.searchClientNoOrg.doGet(`/cross-org-search/${id}`);
    }

    public getPastCrossOrgSearchResults(): Promise<CrossOrgSearch[]> {
        return this.searchClientNoOrg.doGet("/cross-org-searches");
    }

    public deletePastCrossOrgSearches(ids: uuid[]): Promise<void> {
        return this.searchClientNoOrg.doPost("/cross-org-searches-delete", ids);
    }

    public downloadCrossOrgSearchResults(id: uuid): Promise<Blob> {
        return this.searchClientNoOrg.downloadCsvPost(`/cross-org-search-results-csv/${id}`);
    }

    public createWatchlist(config: Partial<WatchlistConfig>): Promise<ThreatSource> {
        return this.searchClient.doPost("/watchlist", config);
    }

    public editWatchlist(watchlistId: uuid, config: WatchlistConfig): Promise<Watchlist> {
        return this.searchClient.doPut(`/watchlist/${watchlistId}`, config);
    }

    public getOrgWatchlists(): Promise<Watchlist[]> {
        return this.searchClient.doGet("/watchlists")
            .then((lists: Watchlist[]) => lists.filter(list => {
                // avoid showing community watchlists anywhere unless feature is loaded
                return this.featuresService.getFeatures().communityWatchlists || !list.communityWatchlist;
            }));
    }

    public editWatchlistItems(id: uuid, items: WatchlistItems, reason: string, replace = true): Promise<EditWatchlistItemsResponse> {
        return this.searchClient.doPost(`/watchlist/${id}/items`, items, { replace, reason });
    }

    public getWatchlistItems(id: uuid): Promise<WatchlistItems> {
        return this.searchClient.doGet(`/watchlist/${id}/items`);
    }

    public getWatchlistItemsCsv(id: uuid): Promise<Blob> {
        return this.searchClient.downloadCsvPost(`/watchlist/${id}/items-csv`);
    }

    public deleteWatchlist(id: uuid): Promise<void> {
        return this.searchClient.doDelete(`/watchlist/${id}`);
    }

    public deleteWatchlistItem(watchlistId: uuid, itemId: uuid): Promise<void> {
        return this.searchClient.doDelete(`/watchlist/${watchlistId}/item/${itemId}`);
    }

    public validateWatchlistItem(type: WatchlistType, item: string): Promise<boolean> {
        return this.searchClientNoOrg.doPost(`/watchlist/items/validate/${type}`, { value: item });
    }

    public addWatchlistExclusion(watchlistId: uuid, item: WatchlistItem): Promise<void> {
        return this.searchClient.doPost(`/watchlist/${ watchlistId }/exclusions`, item);
    }

    public removeWatchlistExclusion(watchlistId: uuid, value: string): Promise<void> {
        return this.searchClient.doDelete(`/watchlist/${ watchlistId }/exclusion/${ value }`);
    }

    /**
     * If the queryString references the pseudo field `asset_tag`, we need to expand the field into a set of conditions
     * on IP/Hostname/Macs.
     */
    private determineFinalQuery(queryString: string): Promise<string> {
        if (queryString.match(ASSET_TAG_QUERY_REGEX)) {
            return this.assetClient.getTransformedAssetTagQuery(queryString);
        }
        return Promise.resolve(queryString);
    }

    private static toSearchParams(request: Partial<SearchRangeRequest>): SearchParams {
        const pageNumber = request.pageNumber || 1;
        const pageSize = request.pageSize || 100;
        const sortField = request.sortField || "timestamp";
        const sortDesc = request.sortDesc ?? true;

        const queryObj = toSearchQuery(request.queryString, {
                begin: typeof request.begin === "string" ? request.begin : request.begin.toISOString(),
                end: typeof request.end === "string" ? request.end : request.end.toISOString(),
            },
            undefined,
            request.include?.id ? request.include : undefined,
            request.exclude?.id ? request.exclude : undefined,
            );
        return toSearchParams(queryObj, pageNumber, pageSize,
            { name: sortField, desc: sortDesc }, request.from);
    }
}
